/* eslint-disable react-hooks/exhaustive-deps */

import { useContext, useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/system';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { CustomCompactContainer, LedgerTable } from 'components';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import CurrencyAdjustedValueViewer from 'components/Spreadsheet/components/CurrencyAdjustedValueViewer';
import FundOwnershipContext from 'context/FundOwnershipContext';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { AMOUNT_ALIAS, COST_BASIS_ALIAS } from 'pages/CapTable/fund-ownership/components/shares-ledger/constants';
import useExchangeRateMap from 'pages/CapTable/fund-ownership/components/shares-ledger/hooks/useExchangeRateMap';
import { shortDate } from 'utilities/dateFormats';
import colConfig from './colConfig';
import { OutstandingProps } from './types';
import calculateOutstandingFromCells from '../../utilities/calculateRegularOutstandingFromCells';

type ConvertibleNoteRow = {
  date: string;
  amount: number;
  loan_value?: number;
};

type RegularRow = {
  date: string;
  shares: number;
  cost_basis: number;
};

type OutstandingRow = ConvertibleNoteRow | RegularRow;

type LedgerCellSubType = Pick<Partial<ICell>, 'value' | 'readOnly' | 'alias'>;

interface LedgerCell {
  loan_value1?: LedgerCellSubType;
  date1?: LedgerCellSubType;
  shares1?: LedgerCellSubType;
  cost_basis1?: LedgerCellSubType;
  cumulative_dividends1?: LedgerCellSubType;
  total_shares_outstanding1?: LedgerCellSubType;
  amount1?: LedgerCellSubType;
}

function adjustIfDifferentCurrency(isDifferentCurrency: boolean, cellData: any, outstandingDate: string) {
  if (isDifferentCurrency) {
    return {
      ...cellData,
      valueViewer: CurrencyAdjustedValueViewer,
      relatedDate: outstandingDate,
    };
  }
  return cellData;
}

const Outstanding = ({
  outstandingShares,
  outstandingCostBasis,
  outstandingAmount,
  doFullValidation,
  setIsValid,
  updateValidationStatus,
  isDisabled,
  modeledAfterConvertibleNote,
  relatedSecurity,
  fundCurrency,
  isDifferentCurrency,
  selectedMeasurementDate,
  format,
  outstandingLoanValue,
  isLoanNote,
  purchasedCells,
  soldCells,
  outstandingValuesForDividends,
}: OutstandingProps) => {
  const [{ captableInfo }] = useStore() as unknown as UseStoreValues;

  const { securities } = captableInfo!;
  const security = securities?.find(sec => sec.id === relatedSecurity);
  const [rows, setRows] = useState<OutstandingRow[]>([]);
  const [ledgerCells, setLedgerCells] = useState<LedgerCell>();
  const { cells } = useContext(FeaturedSpreadsheetContext);
  const { measurementDate } = useContext(FundOwnershipContext);

  const { exchangeRateMap } = useExchangeRateMap({
    fundCurrency,
    format,
    shares: rows,
    selectedMeasurementDate,
    isDifferentCurrency,
    cellKeys: [COST_BASIS_ALIAS, AMOUNT_ALIAS],
  });
  // outstandingDate should be the measurementDate
  const outstandingDate = useMemo(() => (measurementDate ? shortDate(measurementDate) : ''), [measurementDate]);

  const config = useMemo(() => colConfig(modeledAfterConvertibleNote, !!security?.has_dividends, isLoanNote), []);

  // Parse cells
  useEffect(() => {
    if (modeledAfterConvertibleNote) {
      const amount = outstandingAmount ?? 0;

      // Define an object for the row data
      const rowData: OutstandingRow = {
        date: outstandingDate ?? '',
        amount,
      };

      // Define an object for the ledger cells data
      const ledgerCellsData: LedgerCell = {
        date1: { value: outstandingDate ?? '', readOnly: true },
        amount1: adjustIfDifferentCurrency(
          isDifferentCurrency,
          { value: amount, readOnly: true, alias: AMOUNT_ALIAS },
          outstandingDate ?? ''
        ),
      };

      // Include loan_value if loanNote is truthy
      if (isLoanNote) {
        rowData.loan_value = outstandingLoanValue;
        ledgerCellsData.loan_value1 = adjustIfDifferentCurrency(
          isDifferentCurrency,
          { value: outstandingLoanValue, readOnly: true, alias: 'loan_value' },
          outstandingDate ?? ''
        );
      }
      setRows([rowData]);
      setLedgerCells(ledgerCellsData);
    } else {
      const { sharesOutstanding, costBasisOutstanding } = calculateOutstandingFromCells({ purchasedCells, soldCells });

      setRows([
        {
          date: outstandingDate ?? '',
          shares: sharesOutstanding || 0,
          cost_basis: costBasisOutstanding || 0,
        },
      ]);

      const cellData = {
        value: costBasisOutstanding || 0,
        readOnly: true,
        alias: COST_BASIS_ALIAS,
      };

      const costBasis1 = adjustIfDifferentCurrency(isDifferentCurrency, cellData, outstandingDate ?? '');
      const { cumulative_dividends, total_shares_outstanding } = outstandingValuesForDividends;

      setLedgerCells({
        date1: { value: outstandingDate, readOnly: true, alias: 'date' },
        shares1: { value: sharesOutstanding || 0, readOnly: true },
        cost_basis1: costBasis1,
        cumulative_dividends1: {
          value: cumulative_dividends,
          readOnly: true,
        },
        total_shares_outstanding1: {
          value: total_shares_outstanding,
          readOnly: true,
        },
      });
    }
  }, [
    purchasedCells,
    soldCells,
    outstandingAmount,
    outstandingShares,
    outstandingCostBasis,
    cells,
    security,
    measurementDate,
    outstandingDate,
    outstandingValuesForDividends,
  ]);

  return (
    <CustomCompactContainer direction="column" rowSpacing={2.5} wrap="nowrap">
      <Grid>
        <h4>Outstanding</h4>
      </Grid>

      <Grid>
        <LedgerTable
          colConfig={config}
          rows={rows}
          showAddRowButton={false}
          showDeleteColumn={false}
          disabled={isDisabled}
          tableTerms={{
            tableName: 'Outstanding',
            tableSlug: 'outstanding',
            columnName: 'Outstanding',
            pluralColumnName: 'Outstandings',
          }}
          cells={ledgerCells}
          setCells={setLedgerCells}
          doFullValidation={doFullValidation}
          setIsValid={setIsValid}
          updateValidationStatus={updateValidationStatus}
          currency={format.currency}
          exchangeRateMap={exchangeRateMap}
        />
      </Grid>
    </CustomCompactContainer>
  );
};

export default Outstanding;
